import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { ProjectAdmin } from '../components/dashboard/ProjectAdmin';
import { ServiceContactsAdmin } from '../components/dashboard/ServiceContactsAdmin';
import { 
  Users, 
  UserPlus, 
  Trash2, 
  Edit2,
  Mail,
  Shield,
  Eye,
  Send,
  ArrowLeft,
  UserCheck,
  UserX,
  FolderKanban,
  Server,
  LogIn,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Dashboard sections that can be controlled
const DASHBOARD_SECTIONS = [
  { id: 'executive_overview', name: 'Executive Overview', description: 'Tenant, availability, security, spend cards' },
  { id: 'm365_licensing', name: 'Microsoft 365 & Licensing', description: 'Licenses, storage, identity tabs' },
  { id: 'azure_entra', name: 'Azure + Entra ID', description: 'Azure spend, security, governance panels' },
  { id: 'service_status', name: 'Microsoft Service Status', description: 'M365 service health grid' },
  { id: 'internal_services', name: 'Internal Services', description: 'Internal services monitor' },
  { id: 'ticket_snapshot', name: 'Ticket Snapshot', description: 'Jira ticket statistics' },
  { id: 'goals_metrics', name: '2026 Goals & Metrics', description: 'Goals tracker' },
];

export const AdminPage = ({ onBack, onImpersonate }) => {
  const { getAccessToken, isAdmin, isProjectManager, userRole, startImpersonation, user } = useAuth();
  const [activeTab, setActiveTab] = useState(isAdmin ? 'users' : 'projects');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    is_admin: false,
    role: 'user',
    allowed_sections: [],
    send_invite: true,
  });

  const fetchUsers = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      is_admin: false,
      role: 'user',
      allowed_sections: [],
      send_invite: true,
    });
    setEditingUser(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      is_admin: user.is_admin || false,
      role: user.role || (user.is_admin ? 'admin' : 'user'),
      allowed_sections: user.allowed_sections || [],
      send_invite: false,
    });
    setDialogOpen(true);
  };

  const handleSectionToggle = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      allowed_sections: prev.allowed_sections.includes(sectionId)
        ? prev.allowed_sections.filter(id => id !== sectionId)
        : [...prev.allowed_sections, sectionId]
    }));
  };

  const handleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      allowed_sections: DASHBOARD_SECTIONS.map(s => s.id)
    }));
  };

  const handleSelectNone = () => {
    setFormData(prev => ({
      ...prev,
      allowed_sections: []
    }));
  };

  const handleSave = async () => {
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    setIsSaving(true);
    try {
      const token = await getAccessToken();
      const url = editingUser 
        ? `${API_BASE}/api/admin/users/${editingUser.id}`
        : `${API_BASE}/api/admin/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingUser ? 'User updated successfully' : 'User added successfully');
        setDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save user');
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error('Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to remove this user?')) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('User removed successfully');
        fetchUsers();
      } else {
        toast.error('Failed to remove user');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to remove user');
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/admin/users/${user.id}/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`Invite sent to ${user.email}`);
      } else {
        toast.error('Failed to send invite');
      }
    } catch (error) {
      console.error('Failed to send invite:', error);
      toast.error('Failed to send invite');
    }
  };

  const handleActivate = async (user) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/admin/users/${user.id}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`${user.email} has been activated`);
        fetchUsers();
      } else {
        toast.error('Failed to activate user');
      }
    } catch (error) {
      console.error('Failed to activate user:', error);
      toast.error('Failed to activate user');
    }
  };

  const handleDeactivate = async (user) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/api/admin/users/${user.id}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(`${user.email} has been deactivated`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to deactivate user');
      }
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const handleImpersonate = (targetUser) => {
    // Start impersonation and go back to dashboard
    startImpersonation(targetUser);
    if (onImpersonate) {
      onImpersonate(targetUser);
    } else if (onBack) {
      onBack();
    }
  };

  if (!isAdmin && !isProjectManager) {
    return (
      <div className="p-6">
        <Card className="p-6 bg-card/80">
          <div className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">You don't have admin access.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Administration
              </h1>
              <p className="text-muted-foreground text-sm">
                {isAdmin ? 'Manage users and projects' : 'Manage projects'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-border/50">
          {isAdmin && (
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                activeTab === 'users'
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              data-testid="admin-tab-users"
            >
              <Users className="w-4 h-4" />
              User Management
            </button>
          )}
          <button
            onClick={() => setActiveTab('projects')}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
              activeTab === 'projects'
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
            data-testid="admin-tab-projects"
          >
            <FolderKanban className="w-4 h-4" />
            Project Management
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('services')}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                activeTab === 'services'
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
              data-testid="admin-tab-services"
            >
              <Server className="w-4 h-4" />
              Service Contacts
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'projects' ? (
          <ProjectAdmin />
        ) : activeTab === 'services' ? (
          <ServiceContactsAdmin />
        ) : (
          <>
            {/* User Management Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Users ({users.length})
                </h2>
                <p className="text-sm text-muted-foreground">Manage who can access the dashboard</p>
              </div>
              <Button onClick={handleOpenCreate} className="gap-2" data-testid="add-user-btn">
                <UserPlus className="w-4 h-4" />
                Add User
              </Button>
            </div>

        {/* Users Table */}
        <Card className="bg-card/80 backdrop-blur-sm border-border/50">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No users added yet</p>
              <p className="text-sm mt-2">Click "Add User" to grant dashboard access</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Sections</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((targetUser) => (
                  <TableRow key={targetUser.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{targetUser.name || 'No name'}</p>
                        <p className="text-sm text-muted-foreground">{targetUser.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {targetUser.role === 'admin' || targetUser.is_admin ? (
                        <Badge className="bg-primary/10 text-primary">Admin</Badge>
                      ) : targetUser.role === 'project_manager' ? (
                        <Badge className="bg-emerald-500/10 text-emerald-400">Project Mgr</Badge>
                      ) : (
                        <Badge variant="outline">Viewer</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {targetUser.is_admin || targetUser.role === 'project_manager' ? (
                        <span className="text-sm text-muted-foreground">All sections</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {targetUser.allowed_sections?.length || 0} sections
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {targetUser.status === 'active' ? (
                        <Badge variant="outline" className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))]">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-[rgb(var(--warning))]/10 text-[rgb(var(--warning))]">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Log in as User button - only show for non-admin users and not yourself */}
                        {user?.email?.toLowerCase() !== targetUser.email?.toLowerCase() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleImpersonate(targetUser)}
                            title="Log in as this user"
                            className="text-primary hover:text-primary hover:bg-primary/10"
                          >
                            <LogIn className="w-4 h-4" />
                          </Button>
                        )}
                        {targetUser.status !== 'active' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleActivate(targetUser)}
                            title="Activate user"
                            className="text-[rgb(var(--success))] hover:text-[rgb(var(--success))]"
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeactivate(targetUser)}
                            title="Deactivate user"
                            className="text-[rgb(var(--warning))] hover:text-[rgb(var(--warning))]"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResendInvite(targetUser)}
                          title="Send invite email"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(targetUser)}
                          title="Edit user"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(targetUser.id)}
                          title="Remove user"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Add/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                {editingUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? 'Update user permissions and access'
                  : 'Grant dashboard access to a new user'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Email */}
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@therapymgmt.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-9"
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                  data-testid="user-name-input"
                />
              </div>

              {/* Admin Toggle */}
              <div className="space-y-3">
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    role: v,
                    is_admin: v === 'admin',
                  })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Viewer</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.role === 'admin' && 'Full access to all sections and user management'}
                  {formData.role === 'project_manager' && 'Can manage projects and view all dashboard sections'}
                  {formData.role === 'user' && 'Can view assigned dashboard sections'}
                </p>
              </div>

              {/* Section Permissions */}
              {formData.role === 'user' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Dashboard Sections</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                        Select All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleSelectNone}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto border rounded-lg p-3">
                    {DASHBOARD_SECTIONS.map((section) => (
                      <div key={section.id} className="flex items-start space-x-3">
                        <Checkbox
                          id={section.id}
                          checked={formData.allowed_sections.includes(section.id)}
                          onCheckedChange={() => handleSectionToggle(section.id)}
                        />
                        <div className="grid gap-1 leading-none">
                          <Label htmlFor={section.id} className="text-sm font-medium cursor-pointer">
                            {section.name}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {section.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send Invite */}
              {!editingUser && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="send_invite"
                    checked={formData.send_invite}
                    onCheckedChange={(checked) => setFormData({ ...formData, send_invite: checked })}
                  />
                  <Label htmlFor="send_invite" className="text-sm font-medium leading-none cursor-pointer">
                    Send email invite to user
                  </Label>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving} data-testid="save-user-btn">
                {isSaving ? 'Saving...' : (editingUser ? 'Update User' : 'Add User')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}
      </div>
    </div>
  );
};
